// Print a radiology order requisition — the paper form that goes out with the
// order itself, before any study is performed or reported.
//
// It is not a cut-down report: what belongs on a requisition is the order, not
// its outcome. Who the patient is, who asked for the studies, when, on which
// modality, and the list of studies with their priority — everything the
// console needs to know it has the right patient in front of it, and nothing
// about findings, which do not exist yet.
//
// One requisition covers one patient and one department: the studies picked on
// the Order Requisition Print page, however many orders they were raised
// across, print on a single slip that goes to that department's console.
//
// Prints on the same A4 sheet and letterhead as the radiology report
// (utils/reportSheet.js, utils/reportLetterhead.js) so the two documents line
// up in the patient's file.
import { toast } from "react-toastify";
import {
  buildIdBox,
  esc,
  formatAgeGender,
  renderReportSheet,
} from "../../../utils/reportSheet";
import {
  resolveFacilityDetails,
  resolveLogo,
} from "../../../utils/reportLetterhead";
import {
  computeAge,
  formatDateTime,
  withSalutation,
} from "../radiologyOrdersShared";
import { resolveRadiologyDepartment } from "./radiologyDepartments";

const dash = (value) => {
  const s = String(value ?? "").trim();
  return s && s !== "-" ? s : "-";
};

/** The doctor who raised the order, falling back to the patient's own doctor. */
export const resolveReferringDoctor = (primary, patient) => {
  const name = [primary?.doctor, patient?.doctorName, patient?.doctor]
    .map((v) => String(v || "").trim())
    .find((v) => v && v !== "-");
  return name ? withSalutation(name) : "-";
};

// A row joined to the patient record carries `age`; one that only carries a
// date of birth still has an age to print.
const resolveAge = (patient) => patient?.age ?? computeAge(patient?.dob);

// Same room/bed phrasing the orders list shows under the patient's name. Only
// an admitted patient has one; an OPD requisition prints "-".
const formatLocation = (patient) => {
  const room = dash(patient?.roomNo);
  const bed = dash(patient?.bedNo);
  if (room !== "-" && bed !== "-") return `${room} · Bed ${bed}`;
  if (room !== "-") return room;
  if (bed !== "-") return `Bed ${bed}`;
  return "-";
};

// The earliest study on the slip — the requisition is dated when the doctor
// first asked, not when someone got round to printing it.
const earliestOrdered = (orders) =>
  orders
    .map((o) => o?.ordered)
    .filter(Boolean)
    .sort()[0] || null;

const STUDY_COLUMNS = [
  "#",
  "Study / Service",
  "Department",
  "Modality",
  "Priority",
  "Order No",
  "Ordered On",
];

function buildRadiologyOrderHtml({ patient, orders, department, printedAt }) {
  const list = Array.isArray(orders) ? orders : [];
  const primary = list[0] || {};

  const rows = list
    .map(
      (o, i) => `<tr>
        <td>${i + 1}</td>
        <td><strong>${esc(dash(o?.service))}</strong></td>
        <td>${esc(dash(resolveRadiologyDepartment(o)))}</td>
        <td>${esc(dash(o?.modality))}</td>
        <td>${esc(dash(o?.priority))}</td>
        <td>${esc(dash(o?.orderNo))}</td>
        <td>${esc(formatDateTime(o?.ordered))}</td>
      </tr>`,
    )
    .join("");

  return `
    <div class="rp-title">Radiology Order Requisition</div>
    ${buildIdBox(
      [
        ["Patient Name", patient?.name],
        ["Patient ID", patient?.uhid],
        ["Age / Gender", formatAgeGender(resolveAge(patient), patient?.gender)],
        ["Patient Type", patient?.patientType],
        ["Ward / Bed", formatLocation(patient)],
        ["Contact No.", dash(patient?.phone)],
      ],
      [
        ["Department", department ? dash(department) : "All departments"],
        ["Total Studies", String(list.length)],
        ["Ordered On", formatDateTime(earliestOrdered(list))],
        ["Ordering Doctor", resolveReferringDoctor(primary, patient)],
        ["Printed On", formatDateTime(printedAt || Date.now())],
      ],
    )}
    <div class="rp-body">
      <h3>Studies Ordered (${list.length})</h3>
      ${
        list.length
          ? `<table>
              <tbody>
                <tr>${STUDY_COLUMNS.map((c) => `<th>${c}</th>`).join("")}</tr>
                ${rows}
              </tbody>
            </table>`
          : "<p>No studies on this requisition.</p>"
      }
    </div>
  `;
}

/**
 * Open the requisition in a viewer tab with the print dialog already asked for.
 *
 * @param {object} args
 * @param {object} args.patient    the requisition page's patient row
 * @param {Array}  args.orders     the selected studies, all for this patient
 * @param {string} args.department department the slip goes to, or "" when the
 *   selection spans every department
 * @param {Array}  args.brandingData  BrandingContext value (facility logo)
 * @param {object} args.userInfo      PermissionContext userInfo (facility address)
 */
export async function printRadiologyOrderRequisition({
  patient,
  orders,
  department,
  printedAt,
  brandingData,
  userInfo,
}) {
  const list = Array.isArray(orders) ? orders : [];
  if (list.length === 0) {
    toast.info("Select at least one study to print.");
    return;
  }
  try {
    const { address, contact } = resolveFacilityDetails(userInfo);
    const safeName = String(patient?.name || "Patient").replace(
      /[^a-zA-Z0-9]/g,
      "_",
    );
    const safeDept = String(department || "All").replace(/[^a-zA-Z0-9]/g, "_");
    const blob = await renderReportSheet({
      html: buildRadiologyOrderHtml({
        patient,
        orders: list,
        department,
        printedAt,
      }),
      filename: `Radiology_Requisition_${safeName}_${safeDept}.pdf`,
      autoPrint: true,
      letterhead: {
        logoBase64: resolveLogo(brandingData),
        address,
        contact,
        patientId: patient?.uhid,
        // The doctor who raised the order signs it; "Checked By" is left for
        // whoever performs the study to counter-sign on paper.
        preparedBy: resolveReferringDoctor(list[0], patient),
        checkedBy: "",
      },
    });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Please allow pop-ups to print the requisition");
      return;
    }
    // Revoking immediately would pull the file out from under the viewer.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the radiology order requisition PDF:", err);
    toast.error("Could not generate the requisition. Please try again.");
  }
}
